import noop from 'lodash/noop';
import { type Dispatch } from 'redux';
import { type PayloadAction, createAction } from 'typesafe-actions';

import * as Constants from '../constants';
import { getInDomain } from '../domains/externalUtils';

const UseCaseActions = Constants.UseCases.ActionTypes;

// #region community
interface NavigateToCommunityPayload {
  openWindow: boolean;
  eventData: $TSFixMe;
}

export const navigateToCommunity = createAction(
  UseCaseActions.NAVIGATE_TO_COMMUNITY,
  (openWindow = false, eventData: $TSFixMe) => ({ openWindow, eventData }),
)<NavigateToCommunityPayload>();
export type NavigateToCommunityAction = ReturnType<typeof navigateToCommunity>;

interface NavigateToBusinessesPayload {
  openWindow: boolean;
  eventData: object;
}

export const navigateToBusinesses = createAction(
  UseCaseActions.NAVIGATE_TO_BUSINESSES,
  (openWindow = false, eventData: $TSFixMe) => ({ openWindow, eventData }),
)<NavigateToBusinessesPayload>();
export type NavigateToBusinessesAction = ReturnType<typeof navigateToBusinesses>;

interface CommunityLogoutPayload {
  eventData: {
    action: typeof Constants.Community.EventAction.COMMUNITY_LOGOUT;
    view_mode: typeof Constants.Community.ViewMode.COMMUNITY_SSO_NAVIGATE;
    page_view: typeof Constants.Community.PageView.COMMUNITY_REDIRECT;
  };
}

export const communityLogout = createAction(UseCaseActions.COMMUNITY_LOGOUT, () => ({
  eventData: {
    action: Constants.Community.EventAction.COMMUNITY_LOGOUT,
    view_mode: Constants.Community.ViewMode.COMMUNITY_SSO_NAVIGATE,
    page_view: Constants.Community.PageView.COMMUNITY_REDIRECT,
  },
}))<CommunityLogoutPayload>();
export type CommunityLogoutAction = ReturnType<typeof communityLogout>;
// #endregion community

// #region PA
export const handlePremiumAccess = createAction(
  UseCaseActions.HANDLE_PREMIUM_ACCESS,
  (continueSource: boolean) => ({
    continueSource,
  }),
)();
export type HandlePremiumAccessAction = ReturnType<typeof handlePremiumAccess>;

interface InitiatePremiumAccessParams {
  trigger: string | $TSFixMe;
  context: string | $TSFixMe;
  targetId: string;
  targetTrackId: string;
}

/**
 * Action creator for starting the premium access coachmark & video display.
 *
 * @param {string} trigger - currently there's only one option for trigger on web, as defaulted.
 *                           when we start serving video plus, will also have to handle 'noavails'
 * @param {string} context - see domains/ads/rewards/externals.js for valid possibilities
 *                           defaulted to backstage because it's the most common case
 * @param {string} targetId - the pandoraId of the target
 *                            must be passed by caller for target to play on PA start
 * @param {string} targetTrackId - optional pandoraId of a specific track w/i a target source
 */
export const initiatePremiumAccess = createAction(
  UseCaseActions.INITIATE_PREMIUM_ACCESS,
  ({
    trigger = Constants.Ads.Rewards.TriggerTypes.AVAILS,
    context = Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
    targetId,
    targetTrackId,
  }: InitiatePremiumAccessParams) => ({ trigger, context, targetId, targetTrackId }),
)<InitiatePremiumAccessParams>();
export type InitiatePremiumAccessAction = ReturnType<typeof initiatePremiumAccess>;
// #endregion PA

// #region search
interface OpenSearchParams {
  searchTerm: $TSFixMe;
  searchType: string | $TSFixMe;
  searchFilter: string | $TSFixMe;
  routeEntry: boolean | $TSFixMe;
}

export const openSearch = createAction(
  UseCaseActions.OPEN_SEARCH,
  ({
    searchTerm,
    searchType = 'mainSearch',
    searchFilter = 'all',
    routeEntry = false,
  }: OpenSearchParams) => ({
    searchTerm,
    searchType,
    searchFilter,
    routeEntry,
  }),
)<OpenSearchParams>();
export type OpenSearchAction = ReturnType<typeof openSearch>;

type CloseSearchParams = string | $TSFixMe;

interface CloseSearchPayload {
  searchType: CloseSearchParams;
}

export const closeSearch = createAction(
  UseCaseActions.CLOSE_SEARCH,
  (searchType: CloseSearchParams = 'mainSearch') => ({
    searchType,
  }),
)<CloseSearchPayload>();
export type CloseSearchAction = ReturnType<typeof closeSearch>;
// #endregion search

// #region trackers
type LinkClickedParams = $TSFixMe;
interface LinkClickedPayload {
  event: LinkClickedParams;
}
export type LinkClickedAction = PayloadAction<
  typeof UseCaseActions.LINK_CLICKED,
  LinkClickedPayload
>;
export const linkClicked =
  (event: LinkClickedParams) =>
  (dispatch: Dispatch<LinkClickedAction>): void => {
    dispatch({
      type: UseCaseActions.LINK_CLICKED,
      payload: { event },
    });
  };

type ComponentScrolledParams = $TSFixMe;
interface ComponentScrolledPayload {
  event: ComponentScrolledParams;
}
export type ComponentScrolledAction = PayloadAction<
  typeof UseCaseActions.COMPONENT_SCROLLED,
  ComponentScrolledPayload
>;

export const componentScrolled =
  (event: ComponentScrolledParams) =>
  (dispatch: Dispatch<ComponentScrolledAction>): void => {
    dispatch({
      type: UseCaseActions.COMPONENT_SCROLLED,
      payload: { event },
    });
  };

interface PageRenderedPayload {
  event: $TSFixMe;
  eventName: $TSFixMe;
}
export type PageRenderedAction = PayloadAction<
  typeof UseCaseActions.PAGE_RENDERED,
  PageRenderedPayload
>;
export const pageRendered =
  (event: $TSFixMe, eventName: $TSFixMe) =>
  (dispatch: Dispatch<PageRenderedAction>): void => {
    dispatch({
      type: UseCaseActions.PAGE_RENDERED,
      payload: {
        event,
        eventName,
      },
    });
  };

interface LogWebViewModeEventParams extends $TSFixMe {}

export type LogWebViewModeEventAction = PayloadAction<
  typeof UseCaseActions.LOG_WEB_VIEW_MODE_EVENT,
  LogWebViewModeEventParams
>;

export const webViewModeInteraction =
  (eventData: LogWebViewModeEventParams) =>
  (dispatch: Dispatch<LogWebViewModeEventAction>): $TSFixMe => {
    dispatch({
      type: UseCaseActions.LOG_WEB_VIEW_MODE_EVENT,
      payload: eventData,
    });
  };

// #endregion trackers

// #region playback
interface PlayResumeSourceParams {
  id: $TSFixMe;
  pandoraType: $TSFixMe | null;
  forceRestartSourceIfPlaying: boolean | $TSFixMe;
  context: $TSFixMe;
  shouldShuffle: boolean | $TSFixMe;
  index: $TSFixMe;
  sortOrder: $TSFixMe | null;
  trackToken: $TSFixMe | null;
  dontIntercept: boolean | $TSFixMe;
  pandoraId: $TSFixMe;
  playSourceUsingPandoraId: $TSFixMe;
  pageUrl: string | $TSFixMe;
  stationId: string | $TSFixMe;
}

// This is a wrapper function that handles playing/resuming sources within the same
// session for a user. It either calls playSource to start new sources from
// the beginning or intentPlayTrack to resume paused sources from the correct timestamp,
// based on the state of what is currently playing.
// forceRestartSourceIfPlaying is used when tracks are playing amongst their parent sources and
// we want to make sure we start a song source rather than resume the song within the parent source
export const playResumeSource = createAction(
  UseCaseActions.PLAY_RESUME_SOURCE,
  ({
    id,
    pandoraType = null,
    forceRestartSourceIfPlaying = false,
    context,
    shouldShuffle = false,
    index,
    sortOrder = null,
    trackToken = null,
    dontIntercept = false,
    pandoraId,
    playSourceUsingPandoraId,
    pageUrl = '',
    stationId = '',
  }: PlayResumeSourceParams) => ({
    id,
    pandoraType,
    forceRestartSourceIfPlaying,
    context,
    shouldShuffle,
    index,
    sortOrder,
    trackToken,
    pandoraId,
    playSourceUsingPandoraId,
    pageUrl,
    stationId,
    dontIntercept,
  }),
)<PlayResumeSourceParams>();
export type PlayResumeSourceAction = ReturnType<typeof playResumeSource>;

interface PauseTrackParams {
  track: $TSFixMe;
  callPlaybackPaused: boolean | $TSFixMe;
  logPlaybackActionsAnalytics: boolean | $TSFixMe;
  isSimStreamViolation: boolean | $TSFixMe;
  isExplicit: boolean | $TSFixMe;
}

export const pauseTrack = createAction(
  UseCaseActions.PAUSE_TRACK,
  (
    {
      track,
      callPlaybackPaused = true,
      logPlaybackActionsAnalytics = true,
      isSimStreamViolation = false,
      isExplicit = true,
    }: PauseTrackParams = {} as PauseTrackParams, // $TSFixMe
  ) => ({
    track,
    callPlaybackPaused,
    logPlaybackActionsAnalytics,
    isSimStreamViolation,
    isExplicit,
  }),
)<PauseTrackParams>();
export type PauseTrackAction = ReturnType<typeof pauseTrack>;

interface ReplayTrackParams {
  sourceId: $TSFixMe;
  trackToken: $TSFixMe;
  eventSource: $TSFixMe;
}

export const replayTrack = createAction(
  UseCaseActions.REPLAY_TRACK,
  (params: ReplayTrackParams) => params,
)<ReplayTrackParams>();
export type ReplayTrackAction = ReturnType<typeof replayTrack>;

interface ScrubTrackParams {
  value: $TSFixMe;
  relativeValue: $TSFixMe;
  callback: $TSFixMeFunction;
}

export const scrubTrack = createAction(
  UseCaseActions.SCRUB_TRACK,
  ({ value, relativeValue, callback = noop }: ScrubTrackParams) => ({
    value,
    relativeValue,
    callback,
  }),
)<ScrubTrackParams>();
export type ScrubTrackAction = ReturnType<typeof scrubTrack>;

interface SkipTrackBackParams {
  sourceId: $TSFixMe;
  index: $TSFixMe;
}

export const skipTrackBack = createAction(
  UseCaseActions.SKIP_TRACK_BACK,
  (params: SkipTrackBackParams) => params,
)<SkipTrackBackParams>();
export type SkipTrackBackAction = ReturnType<typeof skipTrackBack>;

interface SkipTrackForwardParams {
  sourceId: $TSFixMe;
  parentAction: $TSFixMe;
  forceNewPlaylist: boolean | $TSFixMe;
  skipType: string | $TSFixMe;
  type: $TSFixMe;
  index: $TSFixMe;
  modePandoraId: $TSFixMe;
}

export const skipTrackForward = createAction(
  UseCaseActions.SKIP_TRACK_FORWARD,
  ({
    sourceId,
    parentAction,
    forceNewPlaylist = false,
    skipType = Constants.Playback.FragmentRequestReasons.SKIP,
    type,
    index,
    modePandoraId,
  }: SkipTrackForwardParams) => ({
    sourceId,
    forceNewPlaylist,
    skipType,
    type,
    parentAction,
    index,
    modePandoraId,
  }),
)<SkipTrackForwardParams>();
export type SkipTrackForwardAction = ReturnType<typeof skipTrackForward>;

export const skipTrackForwardCancel = createAction(UseCaseActions.SKIP_TRACK_FORWARD_CANCEL)();
export type SkipTrackForwardCancelAction = ReturnType<typeof skipTrackForwardCancel>;
// #endregion playback

// #region feedback
interface AddTrackFeedbackParams {
  trackToken: string;
  sourceId: string;
  skip: boolean;
  isPositive: boolean;
}

/**
 * action notifying of the intent to provide feedback
 *
 * @param {boolean} skip - whether or not to skip track
 * @param {boolean} isPositive - true for thumb up, false for thumb down
 */
export const addTrackFeedback = createAction(
  Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK,
  ({ trackToken, sourceId, skip = false, isPositive }: AddTrackFeedbackParams) => ({
    trackToken,
    sourceId,
    skip,
    isPositive,
  }),
)<AddTrackFeedbackParams>();
export type AddTrackFeedbackAction = ReturnType<typeof addTrackFeedback>;

interface RemoveTrackFeedbackParams {
  trackToken: string;
  sourceId: string;
  isPositive: boolean;
}

/**
 * action notifying of the intent to removing feedback
 *
 * @param  {boolean} isPositive - true for thumb up, false for thumb down
 */
export const removeTrackFeedback = createAction(
  Constants.UseCases.ActionTypes.REMOVE_TRACK_FEEDBACK,
  (params: RemoveTrackFeedbackParams) => params,
)<RemoveTrackFeedbackParams>();
export type RemoveTrackFeedbackAction = ReturnType<typeof removeTrackFeedback>;

interface ResetPasswordAndReauthParams {
  resetToken: $TSFixMe;
  newPassword: $TSFixMe;
  router: $TSFixMe;
  nextPath: $TSFixMe;
  username: $TSFixMe;
}
// #endregion feedback

// #region listener
interface LoginListenerParams {
  username: $TSFixMe;
  password: $TSFixMe;
  rememberMe: $TSFixMe;
  isUserInitiated: $TSFixMe;
}

export const loginListener = createAction(
  UseCaseActions.LOGIN_LISTENER,
  (params: LoginListenerParams) => params,
)<LoginListenerParams>();
export type LoginListenerAction = ReturnType<typeof loginListener>;

export const autoLogin = createAction(UseCaseActions.AUTO_LOGIN)();
export type AutoLoginAction = ReturnType<typeof autoLogin>;

export const resetPasswordAndReauth = createAction(
  UseCaseActions.RESET_PASSWORD_AND_REAUTH,
  (params: ResetPasswordAndReauthParams) => params,
)<ResetPasswordAndReauthParams>();
export type ResetPasswordAndReauthAction = ReturnType<typeof resetPasswordAndReauth>;

type SignOutParams = $TSFixMe;

interface SignOutPayload {
  postLogoutPath: SignOutParams;
}

export const signOut = createAction(UseCaseActions.SIGN_OUT, (postLogoutPath: SignOutParams) => ({
  postLogoutPath,
}))<SignOutPayload>();
export type SignOutAction = ReturnType<typeof signOut>;

interface SignUpParams {
  username: $TSFixMe;
  password: $TSFixMe;
  birthDay: number | $TSFixMe;
  birthMonth: number | $TSFixMe;
  birthYear: $TSFixMe;
  zipcode: $TSFixMe;
  gender: $TSFixMe;
  rememberMe: $TSFixMe;
}

export const signUp = createAction(
  UseCaseActions.SIGN_UP,
  ({
    username,
    password,
    birthDay = 1,
    birthMonth = 1,
    birthYear,
    zipcode,
    gender,
    rememberMe,
  }: SignUpParams) => ({
    username,
    password,
    birthDay,
    birthMonth,
    birthYear,
    zipcode,
    gender,
    rememberMe,
  }),
)<SignUpParams>();
export type SignUpAction = ReturnType<typeof signUp>;

interface UpgradeParams {
  tierSlug: $TSFixMe;
  subTierSlug: $TSFixMe;
}

export const upgrade = createAction(
  Constants.UseCases.ActionTypes.UPGRADE,
  (params: UpgradeParams) => params,
)<UpgradeParams>();
export type UpgradeAction = ReturnType<typeof upgrade>;

// #endregion listener

// #region ads
interface SkipAudioAdParams {
  sourceId: $TSFixMe;
  skipType: string | $TSFixMe;
}

export const skipAudioAd = createAction(
  UseCaseActions.SKIP_AUDIO_AD,
  ({ sourceId, skipType = Constants.Playback.FragmentRequestReasons.SKIP }: SkipAudioAdParams) => ({
    sourceId,
    skipType,
  }),
)<SkipAudioAdParams>();
export type SkipAudioAdAction = ReturnType<typeof skipAudioAd>;

interface SkipAdPayload {
  track: $TSFixMe;
  reason: $TSFixMe;
}

export const skipAd = createAction(
  UseCaseActions.SKIP_AD,
  (
    track: $TSFixMe,
    reason: string | $TSFixMe = Constants.Playback.FragmentRequestReasons.SKIP,
  ) => ({
    track,
    reason,
  }),
)<SkipAdPayload>();
export type SkipAdAction = ReturnType<typeof skipAd>;
// #endregion ads

// #region stations
interface StartStationParams {
  stationId: string | null;
  pandoraId: string | null;
  rerouteBasedOnTier: boolean;
  errorRecoveryPath: string | null;
  forceReroute: boolean;
  forceNextTrack: boolean;
  forceApi: boolean;
  dontIntercept: boolean;
  initialPlayback: boolean;
  componentName: string | null;
  stationPosition: number | null;
  stationListLength: number | null;
  startingTrackToken: string | null;
  shouldShuffle: boolean;
  pageUrl: string;
  stationCode: $TSFixMe;
  source: $TSFixMe;
  musicId: $TSFixMe;
}

type StartStationPayload = Exclude<StartStationParams, 'stationCode' | 'source' | 'musicId'> & {
  createStationParams: {
    stationCode: StartStationParams['stationCode'];
    source: StartStationParams['source'];
    musicId: StartStationParams['musicId'];
  };
};

/**
 * action notifying of the intent to start a station. Accepts parameters to create the station if the
 * current user doesn't currently have the station
 *
 * @param {string} stationId - the station to play. Only if we are starting a station from a known station
 * @param {string} pandoraId - if it's a new station, use this
 * @param {string} errorRecoveryPath - reroute to this URL on failure
 * @param {boolean} forceReroute - whether to force a URL reroute to nowplaying on this station select
 * @param {boolean} forceNextTrack - whether to force the next track to play or just continue the previously playing track
 * @param {boolean} forceApi - whether to force setStationPlaylist to clear the current playlist tracks and fetch new ones
 * @param {boolean} rerouteBasedOnTier - whether to do a URL reroute to nowplaying on this station select based on the listener's tier
 * @param {boolean} dontIntercept - whether to block the interception of this action
 * @param {boolean} initialPlayback - whether or not this is the first playback of the listening session
 * @param {string} componentName - the name of the component, used by Stats middleware
 * @param {number} stationPosition - the position in the listener's station list
 * @param {number} stationListLength - length of stations in the station list or search results that user had available
 * @param {string} startingTrackToken - the specific track to start playback from
 * @param {boolean} shouldShuffle - if true will start a shuffle station and retrieve the shuffle station id if necessary
 * @param {string} pageUrl - populated when starting a station from a backstage page for branch deep linking purposes
 * @param {object} createStationParams - additional parameters used to create a new station { stationCode, source, musicId }
 *
 * example usage for when startStation is being used for a station the user already has in her stations:
 * startStation({stationId})
 *
 * example usage for when startStation is being used for a station the user may not already have the station:
 * in this case stationCode is used instead of stationId. StationCode needs to be something like
 * StationConstants.MUSIC_ID + musicId (musicId being the Id
 * of whatever we want to start a station from)
 * startStation({stationCode})
 */
export const startStation = createAction(
  UseCaseActions.START_STATION,
  ({
    stationId = null,
    pandoraId = null,
    rerouteBasedOnTier = false,
    errorRecoveryPath = null,
    forceReroute = false,
    forceNextTrack = false,
    forceApi = false,
    dontIntercept = false,
    initialPlayback = false,
    componentName = null,
    stationPosition = null,
    stationListLength = null,
    startingTrackToken = null,
    shouldShuffle = false,
    pageUrl = '',
    ...createStationParams
  }: StartStationParams) => ({
    stationId,
    pandoraId,
    rerouteBasedOnTier,
    errorRecoveryPath,
    forceReroute,
    forceNextTrack,
    forceApi,
    componentName,
    stationPosition,
    stationListLength,
    createStationParams,
    startingTrackToken,
    shouldShuffle,
    pageUrl,
    dontIntercept,
    initialPlayback,
  }),
)<StartStationPayload>();
export type StartStationAction = ReturnType<typeof startStation>;

interface SetSourceRepeatModeParams {
  sourceId: $TSFixMe;
  mode: $TSFixMe;
}

export const setSourceRepeatMode = createAction(
  UseCaseActions.SET_SOURCE_REPEAT_MODE,
  (params: SetSourceRepeatModeParams) => params,
)<SetSourceRepeatModeParams>();
export type SetSourceRepeatModeAction = ReturnType<typeof setSourceRepeatMode>;

interface SetSourceShuffleModeParams {
  sourceId: $TSFixMe;
  enabled: $TSFixMe;
}

// this function regards in-source-shuffle, different from previous global-shuffle
export const setSourceShuffleMode = createAction(
  UseCaseActions.SET_SOURCE_SHUFFLE_MODE,
  (params: SetSourceShuffleModeParams) => params,
)<SetSourceShuffleModeParams>();
export type SetSourceShuffleModeAction = ReturnType<typeof setSourceShuffleMode>;

interface ChangeInteractiveRadioModeParams {
  stationId: $TSFixMe;
  modeId: $TSFixMe;
  modeIndex: $TSFixMe;
  currentModeId: $TSFixMe;
  modePandoraId: $TSFixMe;
}

interface ChangeInteractiveRadioModePayload extends ChangeInteractiveRadioModeParams {
  currentTrackToken: $TSFixMe;
  musicId: $TSFixMe;
  currentItemIndex: $TSFixMe;
  modePandoraId: $TSFixMe;
}

export type ChangeInteractiveRadioModeAction = PayloadAction<
  typeof UseCaseActions.CHANGE_INTERACTIVE_RADIO_MODE,
  ChangeInteractiveRadioModePayload
>;

export const changeInteractiveRadioMode =
  ({
    stationId,
    modeId,
    modeIndex,
    currentModeId,
    modePandoraId,
  }: ChangeInteractiveRadioModeParams) =>
  (dispatch: Dispatch<ChangeInteractiveRadioModeAction>, getState: $TSFixMeFunction): void => {
    const currentTrackToken = getInDomain('playback.utils.getCurrentTrackToken')(getState());
    const currentItemIndex = getInDomain(
      'playback.selectors.getCurrentItemIndex',
      null,
    )(getState());
    const track = getInDomain('playback.utils.getCurrentTrackFromActivePlaylist')(getState());
    const musicId = track.get('musicId');
    dispatch({
      type: UseCaseActions.CHANGE_INTERACTIVE_RADIO_MODE,
      payload: {
        stationId,
        modeId,
        modeIndex,
        currentModeId,
        currentTrackToken,
        musicId,
        currentItemIndex,
        modePandoraId,
      },
    });
  };
// #endregion

interface GenerateDeepLinkParams {
  sourcePath: $TSFixMe;
  pandoraId: $TSFixMe;
  sourceType: $TSFixMe;
  sourceLocation: $TSFixMe;
}

export const generateDeepLink = createAction(
  Constants.UseCases.ActionTypes.GENERATE_DEEP_LINK,
  (params: GenerateDeepLinkParams) => params,
)<GenerateDeepLinkParams>();
export type GenerateDeepLinkAction = ReturnType<typeof generateDeepLink>;

interface LogApiErrorShownParams {
  messageName: $TSFixMe;
}

export const logApiErrorShown = createAction(
  UseCaseActions.LOG_API_ERROR_SHOWN,
  (params: LogApiErrorShownParams) => params,
)<LogApiErrorShownParams>();
export type LogApiErrorShownAction = ReturnType<typeof logApiErrorShown>;

interface OpenErrorToastParams {
  messageName: $TSFixMe;
}

export const openErrorToast = createAction(
  UseCaseActions.OPEN_ERROR_TOAST,
  (params: OpenErrorToastParams) => params,
)<OpenErrorToastParams>();
export type OpenErrorToastAction = ReturnType<typeof openErrorToast>;
